import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNavGuard } from '../contexts/NavGuardContext';
import { downscaleImage } from '../utils/downscaleImage';
import { sortClientsAlpha, clientLastFirst } from '../utils/clientSort';
import DatePickerButton from '../components/DatePickerButton';
import PaymentsEditor from '../components/PaymentsEditor';
import { Save, Download, ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp, Mail, FolderOpen, Sparkles, Eye, EyeOff, RefreshCw, Paperclip, ImageIcon, X, Languages, FileText, BookOpen, Check, Undo2, AlertTriangle, GitBranch, Wrench, Printer, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import AutoTextarea from '../components/ui/AutoTextarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { SortableList } from '../components/SortableList';
import PhaseTimeline from '../components/PhaseTimeline';
import ContingencyLibraryDialog from '../components/estimates/ContingencyLibraryDialog';
import EmailDefaultsDialog from '../components/email/EmailDefaultsDialog';
import ToolsDrawer from './ToolsDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

export default function EstimateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { registerGuard } = useNavGuard();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [clients, setClients] = useState([]);
  const [showClientPrompt, setShowClientPrompt] = useState(false);  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ to_email: '', subject: '', message: '' });
  const [gmailStatus, setGmailStatus] = useState({ connected: false, gmail_configured: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [senderName, setSenderName] = useState('Kenscho Design');
  const [senderNameOriginal, setSenderNameOriginal] = useState('Kenscho Design');
  const [senderNameSaved, setSenderNameSaved] = useState(false);
  // Bilingual email composition
  const [emailLang, setEmailLang] = useState('en'); // 'en' | 'fr'
  const [emailDefaults, setEmailDefaults] = useState(null); // server-side templates, raw
  const [showEmailDefaults, setShowEmailDefaults] = useState(false);

  // ── Email template rendering ────────────────────────────────────
  // Apply {placeholders} to the user's saved templates so the dialog
  // and outgoing email both show fully-rendered text.
  const renderEmailTemplate = useCallback((template, lang, override = {}) => {
    if (!template) return '';
    const e = override.estimate || estimate || {};
    const name = override.senderName || senderName || 'Kenscho Design';
    const clientName = (e.client?.name || '').trim();
    const clientFirst = clientName.split(/\s+/)[0] || '';
    const map = {
      '{estimate_number}': e.estimate_number || '',
      '{sender_name}': name,
      '{client_name}': clientName,
      '{client_first_name}': clientFirst,
      '{estimate_title}': e.title || '',
    };
    let out = template;
    Object.entries(map).forEach(([k, v]) => { out = out.split(k).join(v); });
    return out;
  }, [estimate, senderName]);

  // Pull subject/message for a given language from already-loaded defaults.
  const buildEmailFromDefaults = useCallback((lang, defaults, override = {}) => {
    const subjT = lang === 'fr' ? defaults?.default_subject_fr : defaults?.default_subject_en;
    const msgT = lang === 'fr' ? defaults?.default_message_fr : defaults?.default_message_en;
    return {
      subject: renderEmailTemplate(subjT, lang, override),
      message: renderEmailTemplate(msgT, lang, override),
    };
  }, [renderEmailTemplate]);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiMode, setAiMode] = useState('full');
  const [aiSectionIdx, setAiSectionIdx] = useState(null);
  const [preAiSnapshot, setPreAiSnapshot] = useState(null);
  const [lastManualSave, setLastManualSave] = useState(null);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [generatingDescs, setGeneratingDescs] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorLang, setEditorLang] = useState('en');
  const [bgTranslating, setBgTranslating] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryChecked, setLibraryChecked] = useState({});
  const [newItemText, setNewItemText] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [frOutOfSync, setFrOutOfSync] = useState(false);
  const [enOutOfSync, setEnOutOfSync] = useState(false);
  const [changeOrders, setChangeOrders] = useState([]);
  const [creatingCo, setCreatingCo] = useState(false);
  const [showToolsDrawer, setShowToolsDrawer] = useState(false);
  const [toolsLinked, setToolsLinked] = useState(false);
  // Section titles shown in the Summary sidebar — only updated on blur of
  // the section title input so it doesn't flicker while typing.


  useEffect(() => {
    fetchEstimate();
    fetchClients();
    fetchGmailStatus();
    fetchChangeOrders();
    checkToolsLinked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchChangeOrders = async () => {
    try {
      const res = await fetch(`${API}/estimates/${id}/change-orders`, { credentials: 'include' });
      if (res.ok) setChangeOrders(await res.json());
    } catch { /* ignore */ }
  };

  const checkToolsLinked = async () => {
    try {
      const res = await fetch(`${API}/estimates/${id}/tools`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setToolsLinked(!!data);
      }
    } catch { /* ignore */ }
  };

  const handleCreateChangeOrder = async () => {
    setCreatingCo(true);
    try {
      const res = await fetch(`${API}/estimates/${id}/change-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: '',
          reason: '',
          sections: [{ title: '', items: [{ description: '', qty: 1, unit_price: 0 }] }],
          tax_gst: estimate.tax_gst,
          tax_qst: estimate.tax_qst,
        }),
      });
      if (res.ok) {
        const co = await res.json();
        toast.success(`Created ${co.change_order_number}`);
        navigate(`/change-orders/${co.change_order_id}`);
      } else {
        toast.error('Failed to create change order');
      }
    } catch {
      toast.error('Failed to create change order');
    } finally {
      setCreatingCo(false);
    }
  };

  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const handleGenerateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      // Persist current edits first so the invoice picks up the latest version
      await handleSave();
      const res = await fetch(`${API}/estimates/${id}/invoices/from-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        const inv = await res.json();
        toast.success(`Created ${inv.invoice_number}`);
        navigate(`/invoices/${inv.invoice_id}`);
      } else {
        toast.error('Failed to create invoice');
      }
    } catch {
      toast.error('Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const fetchEstimate = async () => {
    try {
      const res = await fetch(`${API}/estimates/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEstimate(data);
        const expanded = {};
        (data.sections || []).forEach((_, i) => { expanded[i] = true; });
        setExpandedSections(expanded);
        // Determine initial FR sync state: if no FR content has ever been
        // generated, we're out of sync. Otherwise assume in sync until edits.
        const hasFrContent = !!(
          (data.sections_fr && data.sections_fr.length) ||
          data.title_fr ||
          (data.project_bullets_fr && data.project_bullets_fr.length) ||
          (data.general_provisions_fr && data.general_provisions_fr.length)
        );
        setFrOutOfSync(!hasFrContent);
      } else {
        navigate('/estimates');
      }
    } catch {
      navigate('/estimates');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      // Use the directory aggregator so we get everyone Richard has ever
      // estimated for, not just explicitly-saved clients. Each record is
      // assigned a synthetic `_picker_id` so the dropdown can look it back
      // up on selection (the directory endpoint returns null client_id for
      // people who only exist as embedded data on past estimates).
      const res = await fetch(`${API}/clients/directory`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setClients(data.map((c, i) => ({ ...c, _picker_id: c.client_id || `dir-${i}` })));
      }
    } catch { /* ignore */ }
  };

  const fetchGmailStatus = async () => {
    try {
      const res = await fetch(`${API}/gmail/status`, { credentials: 'include' });
      if (res.ok) setGmailStatus(await res.json());
    } catch { /* ignore */ }
  };

  // Check if current client is new (not saved in library)
  useEffect(() => {
    if (!estimate?.client?.name || !estimate?.client?.email) {
      setShowClientPrompt(false);
      return;
    }
    const isExisting = clients.some(
      c => c.name === estimate.client.name && c.email === estimate.client.email
    );
    setShowClientPrompt(!isExisting);
  }, [estimate?.client?.name, estimate?.client?.email, clients]);

  // Auto-split name into first/last for existing estimates that only have 'name'
  useEffect(() => {
    if (estimate?.client?.name && !estimate?.client?.first_name && !estimate?.client?.last_name) {
      const nameParts = estimate.client.name.trim().split(' ');
      const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      // Use setEstimate directly to avoid triggering hasUnsavedChanges
      setEstimate(prev => ({
        ...prev,
        client: { ...prev.client, first_name: firstName, last_name: lastName }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    try {
      const res = await fetch(`${API}/estimates/${id}/save-as-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: templateName }),
      });
      if (res.ok) {
        toast.success('Saved as template!');
        setShowSaveTemplate(false);
        setTemplateName('');
      } else {
        toast.error('Failed to save template');
      }
    } catch { toast.error('Failed to save template'); }
  };

  const handleConnectGmail = async () => {
    try {
      const res = await fetch(`${API}/gmail/auth-url`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Copy the redirect URI to clipboard so user can paste into
        // Google Cloud Console if they hit a redirect_uri_mismatch.
        try {
          await navigator.clipboard.writeText(data.redirect_uri || '');
        } catch { /* ignore clipboard failures */ }
        toast.message(
          `Redirecting to Google...`,
          { description: `If blocked: whitelist ${data.redirect_uri} in your Google Cloud Console OAuth client.`, duration: 8000 }
        );
        setTimeout(() => { window.location.href = data.auth_url; }, 400);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start Gmail auth');
      }
    } catch { toast.error('Failed to connect Gmail'); }
  };

  const handleSendEmail = async () => {
    if (!emailData.to_email) { toast.error('Enter recipient email'); return; }
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/estimates/${id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...emailData, lang: emailLang }),
      });
      if (res.ok) {
        toast.success('Email sent successfully!');
        setShowEmailDialog(false);
        setEmailData({ to_email: '', subject: '', message: '' });
        // Refresh estimate to get updated status
        fetchEstimate();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch { toast.error('Failed to send email'); }
    finally { setSendingEmail(false); }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) { toast.error('Describe what you need'); return; }
    setAiGenerating(true);
    setPreAiSnapshot(JSON.parse(JSON.stringify(estimate)));
    try {
      const body = { job_description: aiPrompt, mode: aiMode };
      if (aiMode === 'contingencies' && aiSectionIdx !== null) {
        const sec = currentSections[aiSectionIdx];
        body.section_title = sec?.title || '';
        // Pass the section's line items as the actual scope so the AI
        // can write notes specific to what's actually being done — not
        // just generic ones for the section title.
        const items = sec?.items || [];
        body.existing_text = items
          .map((it, idx) => `${idx + 1}. ${(it.description || '').trim()}`)
          .filter(s => s.length > 3) // drop empty rows
          .join('\n') || '';
        // Enrich job_description with the wider estimate context so AI
        // knows the bigger picture (title + project bullets) on top of
        // whatever the user typed in the prompt.
        const ctxBits = [];
        if (estimate?.title) ctxBits.push(`Project: ${estimate.title}`);
        const bullets = currentBullets || [];
        if (bullets.length) {
          ctxBits.push(`Scope highlights:\n- ${bullets.slice(0, 6).filter(Boolean).join('\n- ')}`);
        }
        if (aiPrompt) ctxBits.push(`Additional context: ${aiPrompt}`);
        body.job_description = ctxBits.join('\n\n');
      }
      if (aiMode === 'rewrite') {
        body.existing_text = aiPrompt;
        body.job_description = estimate.title || '';
      }

      let res;
      if (aiMode === 'assist') {
        res = await fetch(`${API}/ai/assist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ instruction: aiPrompt, estimate: estimate, editor_lang: editorLang }),
        });
      } else if (aiMode === 'descriptions') {
        const sec = currentSections[aiSectionIdx];
        res = await fetch(`${API}/ai/descriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ section_title: sec?.title || '', job_context: aiPrompt }),
        });
      } else {
        res = await fetch(`${API}/ai/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
      }

      if (res.ok) {
        const data = await res.json();

        if (aiMode === 'assist' && data.changes) {
          const changes = data.changes;
          const lang = data.lang || editorLang;
          setEstimate(prev => {
            const updated = { ...prev };
            if (lang === 'fr') {
              if (changes.title) updated.title_fr = changes.title;
              if (changes.project_bullets) updated.project_bullets_fr = changes.project_bullets;
              if (changes.sections) updated.sections_fr = changes.sections;
              if (changes.general_provisions) updated.general_provisions_fr = changes.general_provisions;
            } else {
              if (changes.title) updated.title = changes.title;
              if (changes.project_bullets) updated.project_bullets = changes.project_bullets;
              if (changes.sections) updated.sections = changes.sections;
              if (changes.general_provisions) updated.general_provisions = changes.general_provisions;
            }
            return updated;
          });
          toast.success('Changes applied!');
          setShowAIDialog(false);
          setAiPrompt('');
        } else if (aiMode === 'full' && data.sections) {
          setEstimate(prev => ({
            ...prev,
            title: data.title || prev.title,
            project_bullets: data.project_bullets || prev.project_bullets,
            sections: data.sections.map(s => ({
              title: s.title || '',
              items: (s.items || []).map(i => ({
                description: i.description || '',
                qty: i.qty || 1,
                unit_price: i.unit_price || 0,
              })),
              contingencies: s.contingencies || [],
            })),
          }));
          const expanded = {};
          data.sections.forEach((_, i) => { expanded[i] = true; });
          setExpandedSections(expanded);
          toast.success('AI generated estimate sections!');
        } else if (aiMode === 'contingencies' && data.contingencies && aiSectionIdx !== null) {
          setEstimate(prev => {
            const sections = [...prev.sections];
            sections[aiSectionIdx] = {
              ...sections[aiSectionIdx],
              contingencies: [...(sections[aiSectionIdx].contingencies || []), ...data.contingencies],
            };
            return { ...prev, sections };
          });
          toast.success('Notes added!');
        } else if (aiMode === 'descriptions' && data.items && aiSectionIdx !== null) {
          setEstimate(prev => {
            const sections = [...(prev[sectionsKey] || prev.sections || [])];
            sections[aiSectionIdx] = {
              ...sections[aiSectionIdx],
              items: [...sections[aiSectionIdx].items, ...data.items.map(i => ({
                description: i.description || '', qty: i.qty || 1, unit_price: i.unit_price || 0,
              }))],
            };
            return { ...prev, [sectionsKey]: sections };
          });
          toast.success('Descriptions added!');
        } else if (aiMode === 'rewrite' && data.text) {
          setAiPrompt(data.text);
          toast.success('Text improved — copy it where needed');
        }

        if (aiMode !== 'rewrite') {
          setShowAIDialog(false);
          setAiPrompt('');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'AI generation failed');
      }
    } catch (e) {
      toast.error('AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/estimates/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      }
    } catch { /* ignore */ }
    finally { setPreviewLoading(false); }
  }, [id, editorLang]);

  const togglePreview = () => {
    if (!showPreview) {
      if (showToolsDrawer) setShowToolsDrawer(false);  // mutex
      setShowPreview(true);
      loadPreview();
    } else {
      setShowPreview(false);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  // Warn on browser close/refresh with unsaved changes (native prompt).
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // In-app nav guard. When the user tries to leave via sidebar, logout,
  // or any code path that consults NavGuard, we open the Leave dialog and
  // block until the user picks Save / Discard / Stay. The guard returns
  // true to proceed, false to cancel.
  const leaveResolverRef = useRef(null);

  useEffect(() => {
    const unregister = registerGuard(() => {
      if (!hasUnsavedChanges) return Promise.resolve(true);
      return new Promise((resolve) => {
        leaveResolverRef.current = resolve;
        setShowLeaveDialog(true);
      });
    });
    return unregister;
  }, [hasUnsavedChanges, registerGuard]);

  // Back-arrow button pathway (kept for semantics — same dialog, same flow)
  const safeNavigate = (path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowLeaveDialog(true);
    } else {
      navigate(path);
    }
  };

  const resolveLeave = (proceed) => {
    if (leaveResolverRef.current) {
      leaveResolverRef.current(proceed);
      leaveResolverRef.current = null;
    }
  };

  const handleLeaveDiscard = () => {
    setHasUnsavedChanges(false);
    setShowLeaveDialog(false);
    resolveLeave(true);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleLeaveSave = async () => {
    await handleSave();
    setHasUnsavedChanges(false);
    setShowLeaveDialog(false);
    resolveLeave(true);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleLeaveCancel = () => {
    setShowLeaveDialog(false);
    resolveLeave(false);
    setPendingNavigation(null);
  };

  const handleUploadImage = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const original of files) {
        // Downscale large photos (e.g. 4 MB iPhone shots) to ≤ 1600 px on
        // the long edge before upload. Saves storage + upload time.
        const file = await downscaleImage(original);
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/estimates/${id}/attachments`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          const att = await res.json();
          setEstimate(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), att],
          }));
        } else {
          toast.error(`Failed to upload ${original.name}`);
        }
      }
      toast.success('Images uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteAttachment = async (attId) => {
    try {
      await fetch(`${API}/estimates/${id}/attachments/${attId}`, {
        method: 'DELETE', credentials: 'include'
      });
      setEstimate(prev => ({
        ...prev,
        attachments: (prev.attachments || []).filter(a => a.attachment_id !== attId),
      }));
      toast.success('Image removed');
    } catch { toast.error('Failed to remove'); }
  };

  const fetchLibrary = async () => {
    try {
      const res = await fetch(`${API}/contingency-library`, { credentials: 'include' });
      if (res.ok) setLibraryItems(await res.json());
    } catch { /* ignore */ }
  };

  const openLibrary = () => {
    fetchLibrary();
    // Pre-check items already in provisions
    const checked = {};
    currentProvisions.forEach(p => { checked[p] = true; });
    setLibraryChecked(checked);
    setLibraryFilter('all');
    setNewItemText('');
    setNewItemCategory('');
    setShowNewCategory(false);
    setShowLibrary(true);
  };

  const toggleLibraryItem = (text) => {
    setLibraryChecked(prev => ({ ...prev, [text]: !prev[text] }));
  };

  const addCustomLibraryItem = async () => {
    if (!newItemText.trim()) { toast.error('Enter provision text'); return; }
    const cat = (showNewCategory ? newItemCategory.trim() : newItemCategory) || 'Custom';
    if (!cat) { toast.error('Select a category'); return; }
    try {
      const res = await fetch(`${API}/contingency-library`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ text: newItemText.trim(), category: cat }),
      });
      if (res.ok) {
        const item = await res.json();
        setLibraryItems(prev => [...prev, item]);
        setLibraryChecked(prev => ({ ...prev, [item.text]: true }));
        setNewItemText('');
        toast.success('Added to library');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add');
      }
    } catch { toast.error('Failed to add'); }
  };

  const applyLibrarySelections = () => {
    const selected = Object.entries(libraryChecked).filter(([_, v]) => v).map(([text]) => text);
    updateField(provisionsKey, selected);
    setShowLibrary(false);
    toast.success(`${selected.length} provision${selected.length !== 1 ? 's' : ''} applied`);
  };

  const handleUndoAI = () => {
    if (preAiSnapshot) {
      setEstimate(preAiSnapshot);
      setPreAiSnapshot(null);
      toast.success('Reverted to before AI changes');
    }
  };

  const handleTranslate = async (opts = {}) => {
    setTranslating(true);
    setPreAiSnapshot(JSON.parse(JSON.stringify(estimate)));
    // Direction defaults to the current editor language (edit EN → translate
    // to FR; edit FR → back-translate to EN), but callers can override —
    // e.g. the auto-sync-on-language-switch effect always wants EN→FR
    // regardless of which language the editor was just toggled to.
    const srcLang = opts.source_lang ?? (editorLang === 'fr' ? 'fr' : 'en');
    const tgtLang = opts.target_lang ?? (srcLang === 'fr' ? 'en' : 'fr');
    const tgtLabel = tgtLang === 'fr' ? 'French' : 'English';
    // 3-minute safety cap so the request can't hang forever behind the
    // ingress proxy on very large estimates. Chunked backend usually
    // finishes in 20-60s.
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 180_000);
    try {
      // Persist current editor state first so the backend translates the
      // latest content (not just what's already in the DB).
      await handleSave();
      const res = await fetch(`${API}/ai/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ estimate_id: id, source_lang: srcLang, target_lang: tgtLang }),
        signal: ctl.signal,
      });
      if (res.ok) {
        const data = await res.json();
        const failures = data._failures || [];
        const stats = data._cache_stats || {};
        delete data._failures;
        delete data._cache_stats;
        setEstimate(prev => ({ ...prev, ...data }));
        setHasUnsavedChanges(false);
        // Refresh BOTH signature baselines so neither direction re-flags
        // immediately after we merge the translate response.
        const merged = { ...estimate, ...data };
        enSigRef.current = JSON.stringify({
          t: merged.title,
          s: merged.sections,
          b: merged.project_bullets,
          p: merged.general_provisions,
          pt: merged.payment_terms,
        });
        frSigRef.current = JSON.stringify({
          t: merged.title_fr,
          s: merged.sections_fr,
          b: merged.project_bullets_fr,
          p: merged.general_provisions_fr,
          pt: merged.payment_terms_fr,
        });
        if (tgtLang === 'fr') setFrOutOfSync(false);
        else setEnOutOfSync(false);
        if (failures.length === 0) {
          const t = stats.translated_sections || 0;
          const r = stats.reused_sections || 0;
          if (t === 0 && r > 0) {
            toast.success('Already in sync — nothing changed.');
          } else if (r > 0) {
            toast.success(`Translated ${t} section${t !== 1 ? 's' : ''} (${r} unchanged, reused from cache)`);
          } else {
            toast.success(t > 0 ? `Translated ${t} section${t !== 1 ? 's' : ''} to ${tgtLabel}` : `Translated to ${tgtLabel}!`);
          }
        } else {
          const parts = failures.slice(0, 3).map(f => f.part).join(', ');
          const syncLabel = tgtLang === 'fr' ? 'Sync FR' : 'Sync EN';
          toast.warning(
            `Translated with ${failures.length} issue${failures.length > 1 ? 's' : ''}: ${parts}${failures.length > 3 ? '…' : ''}. Click ${syncLabel} again to retry.`,
            { duration: 7000 }
          );
        }
      } else {
        let detail = 'Translation failed';
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        toast.error(detail, { duration: 6000 });
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        toast.error('Translation timed out after 3 minutes. Try again, or split very large estimates.', { duration: 7000 });
      } else {
        toast.error('Translation request failed — check your connection and try again.');
      }
    } finally {
      clearTimeout(timer);
      setTranslating(false);
    }
  };

  const handleAIDescriptions = async (sIdx) => {
    setGeneratingDescs(sIdx);
    try {
      const section = estimate.sections[sIdx];
      const res = await fetch(`${API}/ai/descriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ section_title: section.title, job_context: estimate.title }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.items) {
          setEstimate(prev => {
            const sections = [...prev.sections];
            sections[sIdx] = {
              ...sections[sIdx],
              items: [...sections[sIdx].items, ...data.items.map(i => ({
                description: i.description || '',
                qty: i.qty || 1,
                unit_price: i.unit_price || 0,
              }))],
            };
            return { ...prev, sections };
          });
          toast.success('Descriptions added!');
        }
      } else {
        toast.error('Failed to generate descriptions');
      }
    } catch { toast.error('Failed to generate'); }
    finally { setGeneratingDescs(null); }
  };

  const handleSave = useCallback(async () => {
    if (!estimate) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/estimates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: estimate.title,
          client: estimate.client,
          project_bullets: estimate.project_bullets,
          sections: estimate.sections,
          status: estimate.status,
          phase: estimate.phase,
          validity_days: estimate.validity_days,
          tax_gst: estimate.tax_gst,
          tax_qst: estimate.tax_qst,
          payment_terms: estimate.payment_terms,
          notes: estimate.notes,
          general_provisions: estimate.general_provisions || [],
          deposit_paid: estimate.deposit_paid,
          payments: estimate.payments,
          title_fr: estimate.title_fr,
          project_bullets_fr: estimate.project_bullets_fr,
          sections_fr: estimate.sections_fr,
          general_provisions_fr: estimate.general_provisions_fr,
          payment_terms_fr: estimate.payment_terms_fr,
        }),
      });
      if (res.ok) {
        // Don't replace estimate state — just confirm the save
        // Only merge updated_at so we don't overwrite user's live edits
        const updated = await res.json();
        setEstimate(prev => ({ ...prev, updated_at: updated.updated_at }));
        if (showPreview) loadPreview();
        return true;
      } else {
        toast.error('Failed to save');
        return false;
      }
    } catch {
      toast.error('Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  }, [estimate, id, showPreview, loadPreview]);

  const handleManualSave = async () => {
    const ok = await handleSave();
    if (ok) {
      setLastManualSave(new Date());
      setHasUnsavedChanges(false);
      toast.success('Saved');
    }
  };

  const handleRevert = async () => {
    try {
      const res = await fetch(`${API}/estimates/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEstimate(data);
        setShowRevertDialog(false);
        setPreAiSnapshot(null);
        const hasFrContent = !!(
          (data.sections_fr && data.sections_fr.length) ||
          data.title_fr ||
          (data.project_bullets_fr && data.project_bullets_fr.length) ||
          (data.general_provisions_fr && data.general_provisions_fr.length)
        );
        enSigRef.current = JSON.stringify({
          t: data.title,
          s: data.sections,
          b: data.project_bullets,
          p: data.general_provisions,
          pt: data.payment_terms,
        });
        frSigRef.current = JSON.stringify({
          t: data.title_fr,
          s: data.sections_fr,
          b: data.project_bullets_fr,
          p: data.general_provisions_fr,
          pt: data.payment_terms_fr,
        });
        setFrOutOfSync(!hasFrContent);
        setEnOutOfSync(false);
        setHasUnsavedChanges(false);
        toast.success('Reverted to last saved version');
      }
    } catch { toast.error('Failed to revert'); }
  };

  // Auto-save every 30 seconds (silent)
  useEffect(() => {
    if (!estimate || loading) return;
    const interval = setInterval(() => {
      handleSave();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSave, loading]);

  // Track EN and FR content changes to mark the OTHER direction as out of sync.
  // We skip the first render so freshly-loaded estimates keep their current state.
  const enContentSig = estimate ? JSON.stringify({
    t: estimate.title,
    s: estimate.sections,
    b: estimate.project_bullets,
    p: estimate.general_provisions,
    pt: estimate.payment_terms,
  }) : null;
  const frContentSig = estimate ? JSON.stringify({
    t: estimate.title_fr,
    s: estimate.sections_fr,
    b: estimate.project_bullets_fr,
    p: estimate.general_provisions_fr,
    pt: estimate.payment_terms_fr,
  }) : null;
  const enSigRef = useRef(enContentSig);
  const frSigRef = useRef(frContentSig);
  useEffect(() => {
    if (loading || !estimate) return;
    if (enSigRef.current === null) {
      enSigRef.current = enContentSig;
      return;
    }
    if (enSigRef.current !== enContentSig) {
      enSigRef.current = enContentSig;
      setFrOutOfSync(true);
    }
  }, [enContentSig, loading, estimate]);
  useEffect(() => {
    if (loading || !estimate) return;
    if (frSigRef.current === null) {
      frSigRef.current = frContentSig;
      return;
    }
    if (frSigRef.current !== frContentSig) {
      frSigRef.current = frContentSig;
      // FR content changed while user was in the FR editor → EN needs a
      // back-translation. We only flag this when the user is actively editing
      // FR, otherwise programmatic updates (translate response merge, initial
      // load) would spuriously mark EN out of sync.
      if (editorLang === 'fr') setEnOutOfSync(true);
    }
  }, [frContentSig, loading, estimate, editorLang]);

  // French and the FR content is out of date with the English master.
  useEffect(() => {
    if (loading || !estimate || translating) return;
    if (editorLang === 'fr' && frOutOfSync) {
      toast.info('Auto-syncing French version…');
      // Explicit EN→FR — auto-sync is triggered when the FR side is stale
      // relative to the EN master, so direction must NOT depend on the
      // editor language (which is already 'fr' at this point).
      handleTranslate({ source_lang: 'en', target_lang: 'fr' });
    }
    // Intentionally only trigger on language switch, not every dep change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorLang]);

  const handleExportPdf = async () => {
    try {
      const res = await fetch(`${API}/estimates/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${estimate.estimate_number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintPdf = async () => {
    try {
      // Persist any unsaved edits so the print view reflects the latest state.
      if (hasUnsavedChanges) await handleSave();
      const res = await fetch(`${API}/estimates/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Failed to load PDF for printing');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab; most browsers will render the PDF inline and we
      // trigger the print dialog once it's loaded. Revoke the blob URL a
      // few minutes later to keep the tab functional if the user re-prints.
      const win = window.open(url, '_blank');
      if (!win) {
        toast.error('Pop-up blocked — allow pop-ups for this site to use Print.');
        URL.revokeObjectURL(url);
        return;
      }
      const tryPrint = () => {
        try { win.focus(); win.print(); } catch { /* swallow */ }
      };
      // Chrome/Edge inline PDF viewer needs a moment before print() works.
      win.addEventListener('load', () => setTimeout(tryPrint, 400));
      // Fallback if the load event doesn't fire for the inline PDF viewer.
      setTimeout(() => { if (!win.closed) tryPrint(); }, 1500);
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } catch {
      toast.error('Failed to print PDF');
    }
  };

  // ─── Language-aware field helpers ──────────────────────────────
  const sectionsKey = editorLang === 'fr' ? 'sections_fr' : 'sections';
  const bulletsKey = editorLang === 'fr' ? 'project_bullets_fr' : 'project_bullets';
  const titleKey = editorLang === 'fr' ? 'title_fr' : 'title';
  const provisionsKey = editorLang === 'fr' ? 'general_provisions_fr' : 'general_provisions';
  const paymentKey = editorLang === 'fr' ? 'payment_terms_fr' : 'payment_terms';

  const currentSections = estimate?.[sectionsKey] || estimate?.sections || [];
  const currentBullets = estimate?.[bulletsKey] || estimate?.project_bullets || [];
  const currentTitle = estimate?.[titleKey] || estimate?.title || '';
  const currentProvisions = estimate?.[provisionsKey] || estimate?.general_provisions || [];
  const currentPaymentTerms = estimate?.[paymentKey] || estimate?.payment_terms || '';

  // Keep summaryTitles length in sync with sections count (preserves
  // committed titles, fills in new rows with whatever is already in state).
  useEffect(() => {
    // Summary labels are now driven directly from section.title — kept
    // effect slot intentionally empty to preserve hook ordering across
    // older persisted state shapes.
  }, [currentSections.length]);

  const updateField = (field, value) => {
    setEstimate(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const updateCurrentTitle = (value) => {
    setEstimate(prev => ({ ...prev, [titleKey]: value }));
    setHasUnsavedChanges(true);
  };

  const updateClient = (field, value) => {
    setEstimate(prev => ({
      ...prev,
      client: { ...prev.client, [field]: value }
    }));
    setHasUnsavedChanges(true);
  };

  const updateSection = (idx, field, value) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[idx] = { ...sections[idx], [field]: value };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const updateItem = (sIdx, iIdx, field, value) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...sections[sIdx].items];
      items[iIdx] = { ...items[iIdx], [field]: field === 'qty' || field === 'unit_price' ? parseFloat(value) || 0 : value };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const addSection = () => {
    setEstimate(prev => ({
      ...prev,
      [sectionsKey]: [...(prev[sectionsKey] || prev.sections || []), {
        title: `Section ${(prev[sectionsKey] || prev.sections || []).length + 1}`,
        items: [
          { description: '', qty: 0, unit_price: 0 },
          { description: 'Labour and Materials', qty: 1, unit_price: 0 },
        ],
        contingencies: []
      }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeSection = (idx) => {
    setEstimate(prev => ({
      ...prev,
      [sectionsKey]: (prev[sectionsKey] || prev.sections || []).filter((_, i) => i !== idx)
    }));
    setHasUnsavedChanges(true);
  };

  const addItem = (sIdx) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...sections[sIdx].items];
      const lastItem = items[items.length - 1];
      const isLastLabour = lastItem && lastItem.description === 'Labour and Materials';
      if (isLastLabour && items.length > 0) {
        items.splice(items.length - 1, 0, { description: '', qty: 0, unit_price: 0 });
      } else {
        items.push({ description: '', qty: 0, unit_price: 0 });
      }
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const removeItem = (sIdx, iIdx) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], items: sections[sIdx].items.filter((_, i) => i !== iIdx) };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const addContingency = (sIdx) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], contingencies: [...(sections[sIdx].contingencies || []), ''] };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const updateContingency = (sIdx, cIdx, value) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const contingencies = [...(sections[sIdx].contingencies || [])];
      contingencies[cIdx] = value;
      sections[sIdx] = { ...sections[sIdx], contingencies };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const removeContingency = (sIdx, cIdx) => {
    setEstimate(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], contingencies: sections[sIdx].contingencies.filter((_, i) => i !== cIdx) };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const addBullet = () => {
    setEstimate(prev => ({
      ...prev,
      [bulletsKey]: [...(prev[bulletsKey] || prev.project_bullets || []), '']
    }));
    setHasUnsavedChanges(true);
  };

  const updateBullet = (idx, value) => {
    setEstimate(prev => {
      const bullets = [...(prev[bulletsKey] || prev.project_bullets || [])];
      bullets[idx] = value;
      return { ...prev, [bulletsKey]: bullets };
    });
    setHasUnsavedChanges(true);
  };

  const removeBullet = (idx) => {
    setEstimate(prev => ({
      ...prev,
      [bulletsKey]: (prev[bulletsKey] || prev.project_bullets || []).filter((_, i) => i !== idx)
    }));
    setHasUnsavedChanges(true);
  };

  const selectSavedClient = (pickerId) => {
    const cl = clients.find(c => c._picker_id === pickerId);
    if (cl) {
      const nameParts = (cl.name || '').trim().split(' ');
      const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      setEstimate(prev => ({
        ...prev,
        client: {
          name: cl.name || '',
          first_name: firstName,
          last_name: lastName,
          company: cl.company || '',
          address1: cl.address1 || '',
          address2: cl.address2 || '',
          postal_code: cl.postal_code || '',
          phone: cl.phone || '',
          email: cl.email || '',
        }
      }));
    }
  };

  const saveAsClient = async () => {
    if (!estimate?.client?.name) return;
    try {
      const res = await fetch(`${API}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(estimate.client),
      });
      if (res.ok) {
        const cl = await res.json();
        setClients(prev => [...prev, { ...cl, _picker_id: cl.client_id }]);
        toast.success('Client saved');
      }
    } catch {
      toast.error('Failed to save client');
    }
  };

  // Backdate this estimate. The backend recomputes the EST-YYYY-MMDD-NN
  // number by counting how many other estimates already exist on that day
  // and assigning the next sequential suffix. Linked change orders get
  // their parent reference and `EST-...-COxx` number re-stamped too.
  const handleBackdate = async (yyyyMmDd) => {
    if (!estimate?.estimate_id || !yyyyMmDd) return;
    try {
      const res = await fetch(`${API}/estimates/${estimate.estimate_id}/backdate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: yyyyMmDd }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not change date');
        return;
      }
      const updated = await res.json();
      setEstimate(prev => ({
        ...prev,
        estimate_number: updated.estimate_number,
        created_at: updated.created_at,
        updated_at: updated.updated_at,
      }));
      toast.success(`Renumbered to ${updated.estimate_number}`);
    } catch {
      toast.error('Could not change date');
    }
  };

  // Calculate totals
  const calcSectionTotal = (section) => {
    return (section.items || []).reduce((sum, item) => sum + (item.qty || 0) * (item.unit_price || 0), 0);
  };

  const subtotal = currentSections.reduce((sum, s) => sum + calcSectionTotal(s), 0);
  const gstAmount = subtotal * (estimate?.tax_gst || 5) / 100;
  const qstAmount = subtotal * (estimate?.tax_qst || 9.975) / 100;
  const grandTotal = subtotal + gstAmount + qstAmount;
  // Itemized payments. Legacy single `deposit_paid` shows as one synthetic
  // row; any edit materializes it into the payments array (deposit_paid→0).
  const payments = (estimate?.payments && estimate.payments.length)
    ? estimate.payments
    : (Number(estimate?.deposit_paid) > 0
        ? [{ id: 'legacy_deposit', date: '', amount: Number(estimate.deposit_paid), method: 'other' }]
        : []);
  const handlePaymentsChange = (arr) => {
    setEstimate(prev => ({ ...prev, payments: arr, deposit_paid: 0 }));
    setHasUnsavedChanges(true);
  };
  const depositPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  // Note: balanceDue used inside the Summary card only applies when there
  // are no change orders. When COs exist, the Revised Contract card owns
  // the deposit / balance-due math (deposit subtracts from revised total).
  const balanceDue = grandTotal - depositPaid;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!estimate) return null;

  return (
    <div className="animate-fade-in" data-testid="estimate-editor">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => safeNavigate('/estimates')}
            className="p-2 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <Input
              data-testid="estimate-title-input"
              value={currentTitle}
              onChange={e => updateCurrentTitle(e.target.value)}
              className="text-xl font-semibold border-none bg-transparent p-0 h-auto focus:ring-0 text-[#1A1D1C]"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            />
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-xs text-[#6B7A73]" data-testid="estimate-number-display">{estimate.estimate_number}</p>
              <DatePickerButton
                value={estimate.created_at}
                onChange={handleBackdate}
                testId="estimate-date"
              />
              {/* EN/FR Toggle */}
              <div className="flex items-center bg-[#F0F3F1] rounded-full p-0.5" data-testid="editor-lang-toggle">
                <button
                  onClick={() => setEditorLang('en')}
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold transition-colors ${
                    editorLang === 'en' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:text-[#1A1D1C]'
                  }`}
                  data-testid="editor-lang-en"
                >
                  EN
                </button>
                <button
                  onClick={() => setEditorLang('fr')}
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold transition-colors ${
                    editorLang === 'fr' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:text-[#1A1D1C]'
                  }`}
                  data-testid="editor-lang-fr"
                >
                  FR
                </button>
              </div>
              {bgTranslating && (
                <span className="text-[10px] text-[#2D6B68] flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Syncing FR...
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2" data-testid="editor-actions">
          {/* Row 1 — Document status & primary flow */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Select value={estimate.status} onValueChange={v => updateField('status', v)}>
              <SelectTrigger className="w-32 bg-white border-[#E6EBE8] rounded-md text-sm" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="save-btn"
              onClick={handleManualSave}
              className={hasUnsavedChanges
                ? "bg-white border border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
                : "bg-[#2D6B68] border border-[#2D6B68] text-white rounded-md px-4 py-2 font-medium"
              }
            >
              <Save className="w-4 h-4 mr-2" />
              {hasUnsavedChanges ? 'Save' : 'Saved'}
            </Button>
            <Button
              data-testid="revert-btn"
              onClick={() => setShowRevertDialog(true)}
              variant="outline"
              className="border-[#E6EBE8] text-[#6B7A73] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Revert
            </Button>
            <Button
              data-testid="new-co-btn"
              onClick={handleCreateChangeOrder}
              disabled={creatingCo}
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50 rounded-md px-4 py-2 font-medium"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              {creatingCo ? 'Creating...' : 'Change Order'}
            </Button>
            <Button
              data-testid="generate-invoice-btn"
              onClick={handleGenerateInvoice}
              disabled={creatingInvoice}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-md px-4 py-2 font-medium"
            >
              <Receipt className="w-4 h-4 mr-2" />
              {creatingInvoice ? 'Creating...' : 'Generate Invoice'}
            </Button>
            <Button
              data-testid="translate-btn"
              onClick={() => handleTranslate()}
              disabled={translating}
              className={translating
                ? "bg-white border border-[#E6EBE8] text-[#6B7A73] rounded-md px-4 py-2 font-medium"
                : (editorLang === 'fr' ? enOutOfSync : frOutOfSync)
                  ? "bg-white border border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
                  : "bg-[#2D6B68] border border-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium"
              }
            >
              <Languages className="w-4 h-4 mr-2" />
              {translating
                ? 'Syncing...'
                : editorLang === 'fr'
                  ? (enOutOfSync ? 'Sync EN' : 'Synced')
                  : (frOutOfSync ? 'Sync FR' : 'Synced')}
            </Button>
            <Button
              data-testid="preview-toggle-btn"
              onClick={togglePreview}
              variant={showPreview ? "default" : "outline"}
              className={showPreview
                ? "bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium"
                : "border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
              }
            >
              {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showPreview ? 'Close Preview' : 'Preview'}
            </Button>
          </div>

          {/* Row 2 — Export & persistence */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              data-testid="export-pdf-btn"
              onClick={handleExportPdf}
              variant="outline"
              className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button
              data-testid="print-pdf-btn"
              onClick={handlePrintPdf}
              variant="outline"
              className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              data-testid="email-btn"
              onClick={async () => {
                // Pull sender name + saved email defaults, then render
                // for the currently-selected email language.
                let resolvedName = senderName || 'Kenscho Design';
                let defaults = emailDefaults;
                try {
                  const r = await fetch(`${API}/email/settings`, { credentials: 'include' });
                  if (r.ok) {
                    defaults = await r.json();
                    resolvedName = (defaults.email_sender_name || resolvedName).trim();
                    setSenderName(resolvedName);
                    setSenderNameOriginal(resolvedName);
                    setEmailDefaults(defaults);
                  }
                } catch { /* keep defaults / sender as-is */ }
                setSenderNameSaved(false);
                // Default email lang to the editor's current language
                const lang = (editorLang || 'en') === 'fr' ? 'fr' : 'en';
                setEmailLang(lang);
                const rendered = buildEmailFromDefaults(lang, defaults || {}, { senderName: resolvedName, estimate });
                setEmailData({
                  to_email: estimate.client?.email || '',
                  subject: rendered.subject,
                  message: rendered.message,
                });
                setShowEmailDialog(true);
              }}
              variant="outline"
              className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              data-testid="save-as-template-btn"
              onClick={() => {
                setTemplateName(estimate.title || '');
                setShowSaveTemplate(true);
              }}
              variant="outline"
              className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </div>

          {/* Row 3 — Assistive & AI */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              data-testid="tools-needed-btn"
              onClick={() => {
                if (showPreview) setShowPreview(false);  // mutex with PDF preview
                setShowToolsDrawer(v => !v);
              }}
              variant="outline"
              className={showToolsDrawer
                ? 'border-[#2D6B68] bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium'
                : 'border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium relative'
              }
            >
              <Wrench className="w-4 h-4 mr-2" />
              Tools Needed
              {toolsLinked && !showToolsDrawer && (
                <span className="ml-2 w-1.5 h-1.5 rounded-full bg-[#2D6B68]" />
              )}
            </Button>
            <Button
              data-testid="ai-assist-btn"
              onClick={() => {
                setAiMode('assist');
                setAiPrompt('');
                setShowAIDialog(true);
              }}
              className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Assist
            </Button>
            {preAiSnapshot && (
              <Button
                data-testid="undo-ai-btn"
                onClick={handleUndoAI}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md px-4 py-2 font-medium"
              >
                <Undo2 className="w-4 h-4 mr-2" />
                Undo AI
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Change Orders strip */}
      {changeOrders.length > 0 && (
        <div
          className="mb-5 bg-amber-50/60 border border-amber-200 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap"
          data-testid="co-strip"
        >
          <GitBranch className="w-4 h-4 text-amber-700 flex-shrink-0" />
          <span className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
            Change Orders
          </span>
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {changeOrders.map(co => (
              <button
                key={co.change_order_id}
                onClick={() => navigate(`/change-orders/${co.change_order_id}`)}
                className="px-2.5 py-1 bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50 text-xs text-amber-900 rounded-md transition-colors"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                data-testid={`co-pill-${co.change_order_id}`}
                title={co.title || co.change_order_number}
              >
                {co.change_order_number.split('-').slice(-1)[0]}
                <span className="ml-1.5 text-[10px] text-amber-600 uppercase">{co.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Split-screen layout: Editor + Preview */}
      <div className={`flex gap-6 ${(showPreview || showToolsDrawer) ? '' : ''}`}>
        {/* Editor Column */}
        <div className={`${(showPreview || showToolsDrawer) ? 'w-1/2' : 'w-full'} transition-all duration-300`}>
          <div className="grid grid-cols-1 gap-8" style={{ gridTemplateColumns: (showPreview || showToolsDrawer) ? '1fr' : '1fr 280px' }}>
            {/* Main Editor */}
            <div className="space-y-6">
          {/* Workflow phase timeline — PARKED. The Status dropdown (top
              right: Draft / Sent / Accepted / Rejected) is the single
              source of truth for now. The phase field (lead / build /
              invoiced / paid / …) is still persisted in the DB and the
              PhaseTimeline component is kept for when we re-enable this. */}
          {false && (
            <PhaseTimeline
              currentPhase={estimate.phase || 'lead'}
              disabled={saving || translating}
              onChange={(phase) => updateField('phase', phase)}
            />
          )}
          {/* Client Info */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Client Information</h2>
              <div className="flex items-center gap-2">
                {clients.length > 0 && (
                  <Select onValueChange={selectSavedClient}>
                    <SelectTrigger className="w-56 text-xs bg-white border-[#E6EBE8] rounded-md" data-testid="saved-client-select">
                      <SelectValue placeholder="Load saved client" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {sortClientsAlpha(clients).map(cl => (
                        <SelectItem key={cl._picker_id} value={cl._picker_id}>
                          {clientLastFirst(cl) || cl.name || cl.email || 'Unnamed'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  variant="ghost"
                  onClick={saveAsClient}
                  className="text-xs text-[#2D6B68] hover:bg-[#F0F3F1]"
                  data-testid="save-client-btn"
                >
                  Save Client
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input data-testid="client-company" placeholder="Company name (optional)" value={estimate.client?.company || ''} onChange={e => updateClient('company', e.target.value)} className="md:col-span-2 bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-first-name" placeholder="First name" value={estimate.client?.first_name || ''} onChange={e => { updateClient('first_name', e.target.value); updateClient('name', `${e.target.value} ${estimate.client?.last_name || ''}`.trim()); }} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-last-name" placeholder="Last name" value={estimate.client?.last_name || ''} onChange={e => { updateClient('last_name', e.target.value); updateClient('name', `${estimate.client?.first_name || ''} ${e.target.value}`.trim()); }} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-email" placeholder="Email" value={estimate.client?.email || ''} onChange={e => updateClient('email', e.target.value)} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-phone" placeholder="Phone" value={estimate.client?.phone || ''} onChange={e => updateClient('phone', e.target.value)} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-address1" placeholder="Address line 1" value={estimate.client?.address1 || ''} onChange={e => updateClient('address1', e.target.value)} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-address2" placeholder="City, Province" value={estimate.client?.address2 || ''} onChange={e => updateClient('address2', e.target.value)} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
              <Input data-testid="client-postal" placeholder="Postal code" value={estimate.client?.postal_code || ''} onChange={e => updateClient('postal_code', e.target.value)} className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
            </div>
            {showClientPrompt && (
              <div className="mt-3 flex items-center gap-3 bg-[rgba(45,107,104,0.06)] rounded-md px-4 py-2.5">
                <p className="text-xs text-[#3A423E] flex-1">New client detected. Save to your client library for reuse on future estimates?</p>
                <button
                  onClick={() => { saveAsClient(); setShowClientPrompt(false); }}
                  className="text-xs font-medium text-[#2D6B68] hover:text-[#235552] whitespace-nowrap"
                  data-testid="prompt-save-client"
                >
                  Save Client
                </button>
                <button
                  onClick={() => setShowClientPrompt(false)}
                  className="text-xs text-[#6B7A73] hover:text-[#1A1D1C]"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>

          {/* Project Scope */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Project Scope</h2>
              <Button variant="ghost" onClick={addBullet} className="text-xs text-[#2D6B68]" data-testid="add-bullet-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Point
              </Button>
            </div>
            <div className="space-y-2">
              <SortableList
                items={currentBullets}
                onReorder={(newOrder) => updateField(bulletsKey, newOrder)}
                getId={(_, i) => `bullet-${i}`}
                renderItem={(bullet, i) => (
                  <div className="flex items-start gap-2">
                    <AutoTextarea
                      data-testid={`bullet-${i}`}
                      value={bullet}
                      onChange={e => updateBullet(i, e.target.value)}
                      minRows={2}
                      className="flex-1 bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                      placeholder="Describe scope item..."
                    />
                    <button onClick={() => removeBullet(i)} className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors" data-testid={`remove-bullet-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Sections */}
          <SortableList
            items={currentSections}
            onReorder={(newOrder) => updateField(sectionsKey, newOrder)}
            getId={(_, i) => `section-${i}`}
            renderItem={(section, sIdx) => (
            <div className="bg-white border border-[#E6EBE8] rounded-md" data-testid={`section-${sIdx}`}>
              <div className="px-6 py-4 border-b border-[#E6EBE8] flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}
                    className="text-[#6B7A73] hover:text-[#1A1D1C] transition-colors"
                  >
                    {expandedSections[sIdx] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <Input
                    data-testid={`section-title-${sIdx}`}
                    value={section.title}
                    onChange={e => updateSection(sIdx, 'title', e.target.value)}
                    className="border-none bg-transparent p-0 h-auto focus:ring-0 text-sm font-medium text-[#1A1D1C]"
                    placeholder="Section title"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#F5F6F5] text-[#1A1D1C] border-[#E6EBE8] rounded-full px-2.5 text-xs font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    ${calcSectionTotal(section).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Badge>
                  {currentSections.length > 1 && (
                    <button onClick={() => removeSection(sIdx)} className="p-1 text-[#6B7A73] hover:text-red-500 transition-colors" data-testid={`remove-section-${sIdx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {expandedSections[sIdx] && (
                <div className="p-6">
                  {/* Line Items Table */}
                  {/* Line Items - Sortable */}
                  <div className="mb-4">
                    <div className="grid grid-cols-[1fr_70px_110px_90px_30px] gap-2 bg-[#F9F9F8] border-b border-[#E6EBE8] py-2 px-3">
                      <span className="text-xs uppercase tracking-wider text-[#6B7A73] font-semibold">Description</span>
                      <span className="text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-center">Qty</span>
                      <span className="text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-center">Unit Price</span>
                      <span className="text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-right">Total</span>
                      <span></span>
                    </div>
                    <SortableList
                      items={section.items || []}
                      onReorder={(newItems) => updateSection(sIdx, 'items', newItems)}
                      getId={(_, iIdx) => `item-${sIdx}-${iIdx}`}
                      renderItem={(item, iIdx) => (
                        <div className={`grid grid-cols-[1fr_70px_110px_90px_30px] gap-2 py-2 px-3 items-start ${iIdx % 2 === 1 ? 'bg-[#F9F9F8]' : ''}`}>
                          <AutoTextarea
                            data-testid={`item-desc-${sIdx}-${iIdx}`}
                            value={item.description}
                            onChange={e => updateItem(sIdx, iIdx, 'description', e.target.value)}
                            minRows={1}
                            className="w-full bg-transparent border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                            placeholder="Item description..."
                            style={{ minHeight: '38px' }}
                          />
                          <Input
                            data-testid={`item-qty-${sIdx}-${iIdx}`}
                            type="number" min="0" step="1"
                            value={item.qty || ''}
                            onChange={e => updateItem(sIdx, iIdx, 'qty', e.target.value)}
                            className="text-center bg-transparent border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                          />
                          <Input
                            data-testid={`item-price-${sIdx}-${iIdx}`}
                            type="number" min="0" step="0.01"
                            value={item.unit_price || ''}
                            onChange={e => updateItem(sIdx, iIdx, 'unit_price', e.target.value)}
                            className="text-center bg-transparent border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                          />
                          <span className="text-sm font-medium text-[#1A1D1C] text-right pt-2" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            ${((item.qty || 0) * (item.unit_price || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <div className="pt-2">
                            {(section.items || []).length > 1 && (
                              <button onClick={() => removeItem(sIdx, iIdx)} className="p-1 text-[#6B7A73] hover:text-red-500 transition-colors" data-testid={`remove-item-${sIdx}-${iIdx}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => addItem(sIdx)} className="text-xs text-[#2D6B68]" data-testid={`add-item-${sIdx}`}>
                      <Plus className="w-3 h-3 mr-1" /> Add Line Item
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setAiMode('descriptions'); setAiSectionIdx(sIdx); setAiPrompt(''); setShowAIDialog(true); }}
                      className="text-xs text-[#2D6B68]"
                      data-testid={`ai-descriptions-${sIdx}`}
                    >
                      <Sparkles className="w-3 h-3 mr-1" /> AI Fill Descriptions
                    </Button>
                  </div>

                  {/* Contingencies */}
                  <div className="mt-4 pt-4 border-t border-[#E6EBE8]">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7A73]">Notes</p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          onClick={() => { setAiMode('contingencies'); setAiSectionIdx(sIdx); setAiPrompt(section.title); setShowAIDialog(true); }}
                          className="text-xs text-[#2D6B68]"
                          data-testid={`ai-contingencies-${sIdx}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" /> AI Generate
                        </Button>
                        <Button variant="ghost" onClick={() => addContingency(sIdx)} className="text-xs text-[#6B7A73]" data-testid={`add-contingency-${sIdx}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <SortableList
                        items={section.contingencies || []}
                        onReorder={(newOrder) => updateSection(sIdx, 'contingencies', newOrder)}
                        getId={(_, cIdx) => `cont-${sIdx}-${cIdx}`}
                        renderItem={(cont, cIdx) => (
                          <div className="flex items-start gap-2">
                            <AutoTextarea
                              data-testid={`contingency-${sIdx}-${cIdx}`}
                              value={cont}
                              onChange={e => updateContingency(sIdx, cIdx, e.target.value)}
                              minRows={1}
                              className="flex-1 bg-[#F9F9F8] border-[#E6EBE8] rounded-md text-xs"
                              placeholder="Note..."
                              style={{ minHeight: '38px' }}
                            />
                            <button onClick={() => removeContingency(sIdx, cIdx)} className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          />

          <Button
            onClick={addSection}
            variant="outline"
            className="w-full border-dashed border-[#C8D4CE] text-[#6B7A73] hover:bg-[#F5F6F5] hover:text-[#2D6B68] rounded-md py-3"
            data-testid="add-section-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Section
          </Button>

          {/* General Provisions */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6" data-testid="general-provisions">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">General Provisions</h2>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  onClick={openLibrary}
                  className="text-xs text-[#2D6B68]"
                  data-testid="open-library-btn"
                >
                  <BookOpen className="w-3.5 h-3.5 mr-1" /> Library
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => updateField(provisionsKey, [...currentProvisions, ''])}
                  className="text-xs text-[#2D6B68]"
                  data-testid="add-provision-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {currentProvisions.length === 0 ? (
                <p className="text-xs text-[#6B7A73]">No general provisions. Add payment terms, exclusions, client responsibilities, etc.</p>
              ) : (
                <SortableList
                  items={currentProvisions}
                  onReorder={(newOrder) => updateField(provisionsKey, newOrder)}
                  getId={(_, i) => `prov-${i}`}
                  renderItem={(prov, i) => (
                    <div className="flex items-start gap-2">
                      <AutoTextarea
                        data-testid={`provision-${i}`}
                        value={prov}
                        onChange={e => {
                          const arr = [...currentProvisions];
                          arr[i] = e.target.value;
                          updateField(provisionsKey, arr);
                        }}
                        minRows={1}
                        className="flex-1 bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                        placeholder="e.g., Payment due upon completion. 25% deposit required."
                        style={{ minHeight: '38px' }}
                      />
                      <button
                        onClick={() => updateField(provisionsKey, currentProvisions.filter((_, j) => j !== i))}
                        className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                />
              )}
            </div>
          </div>

          {/* Attachments */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6" data-testid="attachments-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Attachments
              </h2>
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.heic,.heif,.pdf"
                  className="hidden"
                  onChange={handleUploadImage}
                  data-testid="upload-input"
                />
                <span className="inline-flex items-center gap-1 text-xs text-[#2D6B68] hover:text-[#235552] font-medium cursor-pointer">
                  <ImageIcon className="w-3.5 h-3.5" />
                  {uploading ? 'Uploading...' : 'Add Files'}
                </span>
              </label>
            </div>
            {(estimate.attachments || []).length === 0 ? (
              <p className="text-xs text-[#6B7A73]">No files attached. Upload images or PDFs to include them with the estimate.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(estimate.attachments || []).map(att => {
                  const isPdf = att.content_type === 'application/pdf' || att.original_filename?.toLowerCase().endsWith('.pdf');
                  return (
                    <div key={att.attachment_id} className="relative group rounded-md overflow-hidden border border-[#E6EBE8]" data-testid={`attachment-${att.attachment_id}`}>
                      {isPdf ? (
                        <div className="w-full h-24 bg-[#F9F9F8] flex flex-col items-center justify-center">
                          <FileText className="w-8 h-8 text-[#2D6B68]" />
                          <span className="text-[10px] text-[#6B7A73] mt-1">PDF</span>
                        </div>
                      ) : (
                        <img
                          src={`${API}/files/${att.storage_path}`}
                          alt={att.original_filename}
                          className="w-full h-24 object-cover"
                          crossOrigin="use-credentials"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <button
                        onClick={() => handleDeleteAttachment(att.attachment_id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 bg-white rounded-full text-red-500 hover:bg-red-50 transition-all"
                        data-testid={`delete-att-${att.attachment_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-[10px] text-[#6B7A73] px-2 py-1 truncate">{att.original_filename}</p>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - only show when preview and tools drawer are closed */}
        {!showPreview && !showToolsDrawer && (
        <div className="space-y-6">
          {/* Totals */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="totals-sidebar">
            <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Summary</h3>
            <div className="space-y-2.5 text-sm">
              {(currentSections).map((s, i) => {
                const title = (s.title || '').trim();
                const label = title ? `${i + 1} - ${title}` : `${i + 1} - Section`;
                return (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="text-[#6B7A73] truncate mr-2" title={label}>{label}</span>
                    <span className="text-[#1A1D1C] font-medium whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ${calcSectionTotal(s).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })}
              <div className="border-t border-[#E6EBE8] pt-2.5">
                <div className="flex justify-between">
                  <span className="text-[#6B7A73]">Subtotal</span>
                  <span className="text-[#1A1D1C] font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    ${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">GST ({estimate.tax_gst}%)</span>
                <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  ${gstAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">QST ({estimate.tax_qst}%)</span>
                <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  ${qstAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="border-t border-[#E6EBE8] pt-2.5">
                <div className="flex justify-between">
                  <span className="text-[#1A1D1C] font-bold">{depositPaid > 0 ? 'Total (inc. tax)' : 'Total'}</span>
                  <span className="text-[#2D6B68] font-bold text-base" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    ${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Deposit Paid (editable) + Balance Due (only when deposit > 0)
                  When this estimate has change orders, the deposit / balance
                  rows live in the Revised Contract card below so the math
                  clearly reflects original + COs − deposit. Avoids the
                  confusing "deposit subtracted from original-only total"
                  appearance when COs are in play. */}
              {changeOrders.length === 0 && (
                <>
                  <div className="pt-2.5 border-t border-[#E6EBE8]">
                    <PaymentsEditor
                      payments={payments}
                      onChange={handlePaymentsChange}
                      testIdPrefix="estimate-payments"
                    />
                  </div>

                  {depositPaid > 0 && (
                    <div className="bg-[#E5F0EE] border border-[#2D6B68] rounded-md p-3 -mx-1 mt-1" data-testid="balance-due-row">
                      <div className="flex justify-between items-baseline">
                        <span className="text-[#1A1D1C] font-bold uppercase text-sm tracking-wider">Balance Due</span>
                        <span className="text-[#2D6B68] font-bold text-lg" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          ${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Revised Contract Total — only shown when this estimate has change orders */}
          {changeOrders.length > 0 && (() => {
            const coTotal = (co) => {
              let s = 0;
              (co.sections || []).forEach(sec =>
                (sec.items || []).forEach(it => { s += (it.qty || 0) * (it.unit_price || 0); })
              );
              const g = s * (co.tax_gst || 5) / 100;
              const q = s * (co.tax_qst || 9.975) / 100;
              return s + g + q;
            };
            const accepted = changeOrders.filter(c => c.status === 'accepted');
            const pending = changeOrders.filter(c => c.status !== 'accepted' && c.status !== 'rejected');
            const acceptedSum = accepted.reduce((sum, c) => sum + coTotal(c), 0);
            const revised = grandTotal + acceptedSum;
            const revisedBalance = revised - depositPaid;
            const fmt = (n) => `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            return (
              <div
                className="bg-amber-50/50 border border-amber-200 rounded-md p-5"
                data-testid="revised-contract-card"
              >
                <div className="flex items-center gap-1.5 mb-4">
                  <GitBranch className="w-3.5 h-3.5 text-amber-700" />
                  <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-[0.15em]">
                    Revised Contract
                  </h3>
                </div>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#6B7A73]">Original total</span>
                    <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {accepted.map(co => {
                    const amt = coTotal(co);
                    return (
                      <div key={co.change_order_id} className="flex justify-between items-baseline gap-2">
                        <span
                          className="text-[#6B7A73] truncate cursor-pointer hover:text-amber-800 hover:underline"
                          onClick={() => navigate(`/change-orders/${co.change_order_id}`)}
                          title={co.title || co.change_order_number}
                          data-testid={`revised-co-row-${co.change_order_id}`}
                        >
                          {co.change_order_number}{co.title ? ` — ${co.title}` : ''}
                        </span>
                        <span
                          className={`whitespace-nowrap ${amt < 0 ? 'text-red-600' : 'text-[#1A1D1C]'}`}
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          data-testid={`revised-co-amount-${co.change_order_id}`}
                        >
                          {amt >= 0 ? '+' : ''}{fmt(amt)}
                        </span>
                      </div>
                    );
                  })}
                  {accepted.length === 0 && (
                    <div className="flex justify-between text-xs text-[#6B7A73] italic">
                      <span>No accepted change orders yet</span>
                    </div>
                  )}
                  <div className="border-t border-amber-200 pt-2.5">
                    <div className="flex justify-between items-baseline">
                      <span className="text-amber-900 font-bold">Revised total</span>
                      <span
                        className="text-amber-900 font-bold text-base"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        data-testid="revised-total"
                      >
                        ${revised.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {pending.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-dashed border-amber-200 text-xs space-y-1.5">
                      <div className="text-[#6B7A73] font-semibold uppercase tracking-wider text-[10px]">
                        Pending COs ({pending.length})
                      </div>
                      {pending.map(co => {
                        const amt = coTotal(co);
                        return (
                          <div key={co.change_order_id} className="flex justify-between items-baseline gap-2 text-[#6B7A73]">
                            <span
                              className="truncate cursor-pointer hover:text-amber-800 hover:underline"
                              onClick={() => navigate(`/change-orders/${co.change_order_id}`)}
                              title={co.title || co.change_order_number}
                              data-testid={`pending-co-row-${co.change_order_id}`}
                            >
                              {co.change_order_number}{co.title ? ` — ${co.title}` : ''}
                            </span>
                            <span
                              className={`whitespace-nowrap ${amt < 0 ? 'text-red-500' : ''}`}
                              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                              data-testid={`pending-co-amount-${co.change_order_id}`}
                            >
                              {amt >= 0 ? '+' : ''}{fmt(amt)}
                            </span>
                          </div>
                        );
                      })}
                      <p className="text-[10px] text-[#6B7A73] mt-1 italic">
                        Not included in revised total until accepted
                      </p>
                    </div>
                  )}

                  {/* Payments + Balance Due — owned by this card whenever COs
                      exist so payments clearly subtract from the revised
                      (CO-inclusive) total, not from the original-only one. */}
                  <div className="pt-3 mt-2 border-t border-amber-200">
                    <PaymentsEditor
                      payments={payments}
                      onChange={handlePaymentsChange}
                      accent="amber"
                      testIdPrefix="estimate-payments"
                    />
                  </div>

                  {depositPaid > 0 && (
                    <div className="bg-amber-100 border border-amber-400 rounded-md p-3 -mx-1 mt-2" data-testid="revised-balance-due-row">
                      <div className="flex justify-between items-baseline">
                        <span className="text-amber-900 font-bold uppercase text-sm tracking-wider">
                          Balance Due
                        </span>
                        <span
                          className="text-amber-900 font-bold text-lg"
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          data-testid="revised-balance-due"
                        >
                          ${revisedBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <p className="text-[10px] text-amber-800 mt-1 italic">
                        Revised total ${revised.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} − payments ${depositPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Settings */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Settings</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">Validity (days)</label>
                <Input
                  data-testid="validity-days-input"
                  type="number"
                  value={estimate.validity_days || 30}
                  onChange={e => updateField('validity_days', parseInt(e.target.value) || 30)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">GST Rate (%)</label>
                <Input
                  data-testid="gst-rate-input"
                  type="number"
                  step="0.01"
                  value={estimate.tax_gst}
                  onChange={e => updateField('tax_gst', parseFloat(e.target.value) || 0)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">QST Rate (%)</label>
                <Input
                  data-testid="qst-rate-input"
                  type="number"
                  step="0.001"
                  value={estimate.tax_qst}
                  onChange={e => updateField('tax_qst', parseFloat(e.target.value) || 0)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm"
                />
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Payment Terms</h3>
            <AutoTextarea
              data-testid="payment-terms-input"
              value={currentPaymentTerms}
              onChange={e => updateField(paymentKey, e.target.value)}
              minRows={4}
              className="bg-white border-[#E6EBE8] rounded-md text-sm"
              placeholder="e.g., Payment due upon completion. 25% deposit required."
            />
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Notes</h3>
            <AutoTextarea
              data-testid="notes-input"
              value={estimate.notes || ''}
              onChange={e => updateField('notes', e.target.value)}
              minRows={3}
              className="bg-white border-[#E6EBE8] rounded-md text-sm"
              placeholder="Internal notes..."
            />
          </div>
        </div>
        )}
      </div>
      </div>

      {/* Tools Needed drawer (mutex with PDF Preview) */}
      {showToolsDrawer && !showPreview && (
        <div className="w-1/2 sticky top-0 h-[calc(100vh-120px)]" data-testid="tools-drawer-panel">
          <ToolsDrawer
            estimateId={id}
            onClose={() => { setShowToolsDrawer(false); checkToolsLinked(); }}
            onNavigateToTools={() => navigate('/tools')}
          />
        </div>
      )}

      {/* PDF Preview Panel */}
      {showPreview && (
        <div className="w-1/2 sticky top-0 h-[calc(100vh-120px)]" data-testid="pdf-preview-panel">
          <div className="bg-white border border-[#E6EBE8] rounded-md h-full flex flex-col">
            <div className="px-4 py-3 border-b border-[#E6EBE8] flex items-center justify-between flex-shrink-0">
              <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">PDF Preview</h3>
              <Button
                variant="ghost"
                onClick={loadPreview}
                disabled={previewLoading}
                className="text-xs text-[#2D6B68] hover:bg-[#F0F3F1]"
                data-testid="refresh-preview-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${previewLoading ? 'animate-spin' : ''}`} />
                {previewLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
            {((editorLang === 'fr' && enOutOfSync) || (editorLang === 'en' && frOutOfSync)) && (
              <div
                data-testid="fr-out-of-sync-banner"
                className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
                <div className="flex-1 text-xs text-amber-900 leading-snug">
                  {editorLang === 'fr' ? (
                    <>
                      <span className="font-semibold">English version is out of sync.</span>{' '}
                      French master has newer edits — click <span className="font-semibold">Sync EN</span> before sending.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">French version is out of sync.</span>{' '}
                      English master has newer edits — click <span className="font-semibold">Sync FR</span> before sending.
                    </>
                  )}
                </div>
                <Button
                  onClick={() => handleTranslate()}
                  disabled={translating}
                  size="sm"
                  className="h-7 px-2.5 text-xs bg-amber-700 hover:bg-amber-800 text-white rounded"
                  data-testid="fr-banner-sync-btn"
                >
                  <Languages className="w-3 h-3 mr-1" />
                  {translating ? 'Syncing...' : (editorLang === 'fr' ? 'Sync EN' : 'Sync FR')}
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              {previewLoading && !pdfUrl ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH&pagemode=none`}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                  data-testid="pdf-preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-[#6B7A73] text-sm">
                  <p>Save your estimate first, then click Refresh to preview.</p>
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-[#E6EBE8] flex-shrink-0">
              <p className="text-xs text-[#6B7A73]">Save your changes, then click Refresh to update the preview.</p>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Save as Template</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">Save this estimate's structure as a reusable template. Client info won't be included.</p>
          <Input
            data-testid="template-name-input"
            placeholder="Template name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)} className="border-[#E6EBE8]">Cancel</Button>
            <Button onClick={handleSaveAsTemplate} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="confirm-save-template-btn">
              <FolderOpen className="w-4 h-4 mr-2" /> Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Email Estimate to Client</DialogTitle>
          </DialogHeader>

          {!gmailStatus.gmail_configured ? (
            <div className="py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                <p className="text-sm text-amber-800 font-medium">Gmail not configured</p>
                <p className="text-xs text-amber-700 mt-1">
                  To send emails, add <code className="bg-amber-100 px-1 rounded">GOOGLE_CLIENT_ID</code> and <code className="bg-amber-100 px-1 rounded">GOOGLE_CLIENT_SECRET</code> to your backend environment variables. 
                  Get these from <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a>.
                </p>
              </div>
            </div>
          ) : !gmailStatus.connected ? (
            <div className="py-4">
              <p className="text-sm text-[#6B7A73] mb-4">Connect your Gmail account to send estimates directly to clients with the PDF attached.</p>
              <Button onClick={handleConnectGmail} className="bg-[#2D6B68] text-white hover:bg-[#235552] w-full" data-testid="connect-gmail-btn">
                <Mail className="w-4 h-4 mr-2" /> Connect Gmail Account
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Language toggle + edit defaults */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">Language</span>
                <div className="inline-flex rounded-md border border-[#E6EBE8] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setEmailLang('en');
                      const r = buildEmailFromDefaults('en', emailDefaults || {}, { senderName, estimate });
                      setEmailData(prev => ({ ...prev, subject: r.subject, message: r.message }));
                    }}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      emailLang === 'en' ? 'bg-[#2D6B68] text-white' : 'bg-white text-[#6B7A73] hover:text-[#1A1D1C]'
                    }`}
                    data-testid="email-lang-en"
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailLang('fr');
                      const r = buildEmailFromDefaults('fr', emailDefaults || {}, { senderName, estimate });
                      setEmailData(prev => ({ ...prev, subject: r.subject, message: r.message }));
                    }}
                    className={`px-3 py-1 text-xs font-medium transition-colors border-l border-[#E6EBE8] ${
                      emailLang === 'fr' ? 'bg-[#2D6B68] text-white' : 'bg-white text-[#6B7A73] hover:text-[#1A1D1C]'
                    }`}
                    data-testid="email-lang-fr"
                  >
                    Français
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEmailDefaults(true)}
                  className="ml-auto text-xs text-[#2D6B68] hover:text-[#1A4F4C] underline-offset-2 hover:underline"
                  data-testid="edit-email-defaults-btn"
                >
                  Edit default templates
                </button>
              </div>

              <div>
                <label className="text-xs text-[#6B7A73] block mb-1 flex items-center justify-between">
                  <span>From (sender name)</span>
                  {senderNameSaved && <span className="text-[10px] text-[#2D6B68]">Saved · used for all future emails</span>}
                </label>
                <Input
                  data-testid="email-sender-name-input"
                  placeholder="Kenscho Design"
                  value={senderName}
                  onChange={e => { setSenderName(e.target.value); setSenderNameSaved(false); }}
                  onBlur={async () => {
                    const trimmed = senderName.trim();
                    if (!trimmed || trimmed === senderNameOriginal) return;
                    try {
                      const res = await fetch(`${API}/email/settings`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ email_sender_name: trimmed }),
                      });
                      if (res.ok) { setSenderNameSaved(true); setSenderNameOriginal(trimmed); }
                    } catch { /* swallow */ }
                  }}
                  className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]"
                />
              </div>
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">To</label>
                <Input
                  data-testid="email-to-input"
                  type="email"
                  placeholder="client@example.com"
                  value={emailData.to_email}
                  onChange={e => setEmailData(prev => ({ ...prev, to_email: e.target.value }))}
                  className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]"
                />
              </div>
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">Subject</label>
                <Input
                  data-testid="email-subject-input"
                  placeholder="Subject"
                  value={emailData.subject}
                  onChange={e => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]"
                />
              </div>
              <div>
                <label className="text-xs text-[#6B7A73] block mb-1">Message</label>
                <Textarea
                  data-testid="email-message-input"
                  rows={4}
                  placeholder="Message to client..."
                  value={emailData.message}
                  onChange={e => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none"
                />
              </div>
              <p className="text-xs text-[#6B7A73]">The estimate PDF will be attached automatically.</p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="border-[#E6EBE8]">Cancel</Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="bg-[#2D6B68] text-white hover:bg-[#235552]"
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Assist Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
              <Sparkles className="w-5 h-5 text-[#2D6B68]" />
              {aiMode === 'assist' ? 'AI Assist' : aiMode === 'full' ? 'AI Estimate Generator' : aiMode === 'contingencies' ? 'AI Notes' : aiMode === 'descriptions' ? 'AI Descriptions' : 'AI Rewrite'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {aiMode === 'assist' && (
              <>
                <p className="text-sm text-[#6B7A73]">
                  Tell me what you need — I can edit text, replace words, fix translations, rewrite sections, or anything else about this estimate.
                  {editorLang === 'fr' && <span className="text-[#2D6B68] font-medium"> (editing French version)</span>}
                </p>
                <Textarea
                  data-testid="ai-assist-prompt"
                  rows={4}
                  placeholder={'e.g., "Replace the word \'soumission\' with \'estimation\' everywhere"\n"Make the project description more detailed"\n"Add a note about winter conditions"'}
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none focus:border-[#2D6B68]"
                />
              </>
            )}
            {aiMode === 'full' && (
              <>
                <p className="text-sm text-[#6B7A73]">
                  Describe the job and Claude will generate sections, line items, pricing, and contingencies.
                </p>
                <Textarea
                  data-testid="ai-prompt-input"
                  rows={5}
                  placeholder="e.g., Garage door installation in a brick wall. Create opening 7'6&quot; x 8'0&quot;, install commercial door with electric operator, interior finish with drywall..."
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none focus:border-[#2D6B68]"
                />
                <div className="bg-[#F9F9F8] border border-[#E6EBE8] rounded-md p-3">
                  <p className="text-xs text-[#6B7A73]">
                    This will replace existing sections with AI-generated content. Save your estimate first if you want to keep current work.
                  </p>
                </div>
              </>
            )}
            {aiMode === 'contingencies' && (
              <>
                <p className="text-sm text-[#6B7A73]">
                  Generate contingency notes for: <strong>{estimate?.sections?.[aiSectionIdx]?.title || 'this section'}</strong>
                </p>
                <Textarea
                  data-testid="ai-contingency-prompt"
                  rows={3}
                  placeholder="Any specific concerns or conditions to include..."
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none focus:border-[#2D6B68]"
                />
              </>
            )}
            {aiMode === 'descriptions' && (
              <>
                <p className="text-sm text-[#6B7A73]">
                  Generate line item descriptions for: <strong>{currentSections[aiSectionIdx]?.title || 'this section'}</strong>
                </p>
                <Textarea
                  data-testid="ai-descriptions-prompt"
                  rows={4}
                  placeholder="Describe what work this section covers, materials to include, any specifics..."
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none focus:border-[#2D6B68]"
                />
                <div className="bg-[#F9F9F8] border border-[#E6EBE8] rounded-md p-3">
                  <p className="text-xs text-[#6B7A73]">
                    New line items will be added to the existing ones. Pricing will be set to $0 for you to fill in.
                  </p>
                </div>
              </>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAIDialog(false)} className="border-[#E6EBE8]">Cancel</Button>
              <Button
                onClick={handleAIGenerate}
                disabled={aiGenerating}
                className="bg-[#2D6B68] text-white hover:bg-[#235552]"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {aiGenerating ? 'Generating...' : 'Generate'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={o => { if (!o) handleLeaveCancel(); }}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">You have unsaved changes to this estimate. Leaving now will lose them. Save first, or discard to continue.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleLeaveCancel} className="border-[#E6EBE8]" data-testid="cancel-leave-btn">Stay on page</Button>
            <Button onClick={handleLeaveDiscard} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="discard-changes-btn">
              Discard Changes
            </Button>
            <Button onClick={handleLeaveSave} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="save-and-leave-btn">
              <Save className="w-4 h-4 mr-2" /> Save &amp; Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <Dialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Revert to Last Save?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">
            This will discard all unsaved changes and restore the estimate to the last saved version
            {lastManualSave && (
              <span className="font-medium text-[#1A1D1C]">
                {' '}(saved {lastManualSave.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {lastManualSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
              </span>
            )}
            {!lastManualSave && estimate?.updated_at && (
              <span className="font-medium text-[#1A1D1C]">
                {' '}(last saved {new Date(estimate.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(estimate.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
              </span>
            )}
            .
          </p>
          <p className="text-xs text-amber-600">This action cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRevertDialog(false)} className="border-[#E6EBE8]">Cancel</Button>
            <Button onClick={handleRevert} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-revert-btn">
              <Undo2 className="w-4 h-4 mr-2" /> Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contingency Library (extracted component — supports drag-and-drop,
          edit/delete of any item including seeded presets). */}
      <ContingencyLibraryDialog
        open={showLibrary}
        onOpenChange={setShowLibrary}
        initialChecked={new Set(estimate?.[provisionsKey] || [])}
        onApply={(selected) => {
          updateField(provisionsKey, selected);
          setShowLibrary(false);
          toast.success(`${selected.length} provision${selected.length !== 1 ? 's' : ''} applied`);
        }}
      />

      <EmailDefaultsDialog
        open={showEmailDefaults}
        onOpenChange={setShowEmailDefaults}
        onSaved={(updated) => {
          setEmailDefaults(prev => ({ ...prev, ...updated }));
          // Refresh the open dialog text from the new defaults
          const r = buildEmailFromDefaults(emailLang, { ...(emailDefaults || {}), ...updated }, { senderName, estimate });
          setEmailData(prev => ({ ...prev, subject: r.subject, message: r.message }));
        }}
      />

    </div>
  );
}
